import React, { lazy } from 'react';
import { appRoutes, AppRoutesObject, ViewRestricted } from './routes.types';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { isReactNativeWebView } from 'src/utils/webview-detection';
import { lazyDefault, lazyRoute } from 'src/utils/lazy-route';
import FullPageWrapper from 'src/containers/FullPageWrapper';
import { LazyAuthenticatedLayout } from 'src/components/ui/layouts/authenticated-layout/lazy-authenticated-layout';

const WrapperLayout = lazy(() => import('src/components/ui/layouts/wrapper-layout'));
const LoginLayout = lazy(() => import('src/components/ui/layouts/login-layout'));
const RegisterLayout = lazy(() => import('src/components/ui/layouts/register-layout'));
const AuthenticatedLayout = LazyAuthenticatedLayout;
const AddSubscriptionLayout = lazy(
  () => import('src/components/ui/layouts/add-subscription-layout/add-subscription-layout'),
);
const SubscriptionCancelationLayout = lazy(
  () =>
    import(
      'src/components/ui/layouts/subscription-cancelation-layout/subscription-cancelation-layout'
    ),
);

const restricted: { [key in ViewRestricted]: ViewRestricted } = {
  guest: 'guest',
  customer: 'customer',
};

const RootPage = () => {
  const isLogged = !AuthUtils.isExpired();
  const location = useLocation();

  if (!isLogged) {
    return <Navigate to={{ pathname: appRoutes.login, search: location.search }} replace={true} />;
  }

  // In mobile WebView, redirect to AI agent instead of dashboard
  if (isReactNativeWebView()) {
    return <Navigate to={appRoutes.aiAgent} replace={true} />;
  }

  return <Navigate to={appRoutes.dashboard} replace={true} />;
};

const routes: AppRoutesObject = {
  root: {
    path: appRoutes.root,
    element: <RootPage />,
    layout: WrapperLayout,
    index: true,
  },
  notFound: {
    path: appRoutes.notFound,
    lazy: () => lazyRoute(() => import('src/pages/NotFoundPage/NotFoundPage'), 'NotFoundPage'),
    layout: WrapperLayout,
  },
  maintenance: {
    path: appRoutes.maintenance,
    lazy: () =>
      lazyRoute(() => import('src/pages/maintenance-page/maintenance-page'), 'MaintenancePage'),
    layout: WrapperLayout,
  },
  impersonate: {
    path: appRoutes.impersonate,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/ImpersonateUserPage/ImpersonateUserPage'),
        'ImpersonateUserPage',
      ),
    layout: FullPageWrapper,
  },
  login: {
    path: appRoutes.login,
    lazy: () => lazyRoute(() => import('src/pages/auth/login-page'), 'LoginPage'),
    layout: LoginLayout,
    restricted: [restricted.customer],
  },
  register: {
    path: appRoutes.register,
    lazy: () => lazyRoute(() => import('src/pages/auth/register-page'), 'RegisterPage'),
    layout: RegisterLayout,
    restricted: [restricted.customer],
  },
  stripePaymentLinkRedirect: {
    path: appRoutes.stripePaymentLinkRedirect,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/stripe-payment-link-redirect-page'),
        'StripePaymentLinkRedirectPage',
      ),
    layout: WrapperLayout,
    restricted: [restricted.guest],
  },
  verifyEmail: {
    path: appRoutes.verifyEmail,
    lazy: () =>
      lazyRoute(() => import('src/pages/verify-email/verify-email-page'), 'VerifyEmailPage'),
    layout: WrapperLayout,
  },
  authVerifyEmail: {
    path: appRoutes.authVerifyEmail,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/verify-email/auth-verify-email-page'),
        'AuthVerifyEmailPage',
      ),
    layout: WrapperLayout,
    restricted: [restricted.guest],
  },
  forgotPassword: {
    path: appRoutes.forgotPassword,
    lazy: () =>
      lazyRoute(() => import('src/pages/auth/forgot-password-page'), 'ForgotPasswordPage'),
    layout: LoginLayout,
    restricted: [restricted.customer],
  },
  forgotPasswordReset: {
    path: appRoutes.forgotPasswordReset,
    lazy: () => lazyRoute(() => import('src/pages/auth/reset-password-page'), 'ResetPasswordPage'),
    layout: LoginLayout,
    restricted: [restricted.customer],
  },
  sharedMedia: {
    path: appRoutes.sharedMedia,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/SharedMedia/SharedRevisionMediaPage'),
        'SharedRevisionMediaPage',
      ),
    layout: FullPageWrapper,
    restricted: [],
  },
  dashboard: {
    path: appRoutes.dashboard,
    lazy: () => lazyRoute(() => import('src/pages/dashboard/dashboard-page'), 'DashboardPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  addSubscriptionPlanSelect: {
    path: appRoutes.addSubscriptionPlanSelect,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/subscription/add-subscription/plan-select-page'),
        'PlanSelectPage',
      ),
    layout: AddSubscriptionLayout,
    restricted: [restricted.guest],
  },
  company: {
    path: appRoutes.company,
    lazy: () => lazyRoute(() => import('src/pages/company/company-page'), 'CompanyPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  companyPersonnel: {
    path: appRoutes.companyPersonnel,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/company-personnel/company-personnel-page'),
        'CompanyPersonnelPage',
      ),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  requestsList: {
    path: appRoutes.requestsList,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/request/requests-list/requests-list-page'),
        'RequestsListPage',
      ),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  request: {
    path: appRoutes.request,
    lazy: () =>
      lazyRoute(() => import('src/pages/request/request-page/request-page'), 'RequestPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  requestRevisions: {
    path: appRoutes.requestRevisions,
    lazy: () =>
      lazyRoute(() => import('src/pages/RevisionsPage/RevisionsPage'), 'RevisionsPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  templatesList: {
    path: appRoutes.templatesList,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/video-templates-list/video-templates-list-page'),
        'VideoTemplatesListPage',
      ),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  createBrand: {
    path: appRoutes.createBrand,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/new-brand-onboarding/NewBrandOnboardingPage'),
        'NewBrandOnboardingPage',
      ),
    layout: FullPageWrapper,
    restricted: [restricted.guest],
  },
  mobileOnboardingCreateBrand: {
    path: appRoutes.mobileOnboardingCreateBrand,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/new-brand-onboarding/MobileOnboardingCreateBrand'),
        'MobileOnboardingCreateBrand',
      ),
    layout: FullPageWrapper,
    restricted: [restricted.guest],
  },
  mobileRequestEdit: {
    path: appRoutes.mobileRequestEdit,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/mobile/mobile-request-edit-page'),
        'MobileRequestEditPage',
      ),
    layout: FullPageWrapper,
    restricted: [restricted.guest],
  },
  brand: {
    path: appRoutes.brand,
    lazy: () => lazyRoute(() => import('src/pages/brand/edit-brand-page'), 'EditBrandPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  brandContentStrategy: {
    path: appRoutes.brandContentStrategy,
    lazy: () =>
      lazyRoute(() => import('src/pages/brand/content-strategy-page'), 'ContentStrategyPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  brandAssistance: {
    path: appRoutes.brandAssistance,
    lazy: () =>
      lazyRoute(() => import('src/pages/brand/brand-assistance-page'), 'BrandAssistancePage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  profile: {
    path: appRoutes.profile,
    lazy: () => lazyRoute(() => import('src/pages/profile/profile-page'), 'ProfilePage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  notificationSettings: {
    path: appRoutes.notificationSettings,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/profile/notification-settings-page'),
        'NotificationSettingsPage',
      ),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  accountOnHold: {
    path: appRoutes.accountOnHold,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/account-on-hold/account-on-hold-page'),
        'AccountOnHoldPage',
      ),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  socialTikTokAuth: {
    path: appRoutes.socialTikTokAuth,
    lazy: () =>
      lazyRoute(() => import('src/pages/social/TikTok/TikTokAuthPage'), 'TikTokAuthPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  socialLogin: {
    path: appRoutes.socialLogin,
    lazy: () =>
      lazyRoute(() => import('src/pages/social/login-callback-page'), 'LoginCallbackPage'),
    restricted: [],
    layout: FullPageWrapper,
  },
  socialProviderCallback: {
    path: appRoutes.socialProviderCallback,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/social/add-social-callback-page'),
        'AddSocialCallbackPage',
      ),
    restricted: [],
    layout: FullPageWrapper,
  },
  socialTracking: {
    path: appRoutes.socialTracking,
    lazy: () => lazyRoute(() => import('src/pages/social/tracking'), 'SocialTrackingPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  socialEntityDetail: {
    path: `${appRoutes.socialTracking}/entity/:entityId`,
    lazy: () => lazyDefault(() => import('src/pages/social/tracking/entity-detail-page')),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  // repurposing: {
  //   path: appRoutes.repurposing,
  //   lazy: async () => {
  //     const { RepurposingPage } = await import('src/pages/repurposing/repurposing-page');
  //     return { Component: RepurposingPage };
  //   },
  //   layout: AuthenticatedLayout,
  //   restricted: [restricted.guest],
  // },
  mediaLibrary: {
    path: appRoutes.mediaLibrary,
    lazy: () => lazyRoute(() => import('../pages/media-library'), 'MediaLibraryPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  aiAgent: {
    path: appRoutes.aiAgent,
    lazy: () => lazyRoute(() => import('src/pages/ai-agent'), 'AIAgentPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  scriptsList: {
    path: appRoutes.scriptsList,
    lazy: () =>
      lazyRoute(() => import('src/pages/scripts/scripts-list-page'), 'ScriptsListPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  createScript: {
    path: appRoutes.createScript,
    lazy: () =>
      lazyRoute(() => import('src/pages/scripts/script-editor-page'), 'ScriptEditorPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  editScript: {
    path: appRoutes.editScript,
    lazy: () =>
      lazyRoute(() => import('src/pages/scripts/script-editor-page'), 'ScriptEditorPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  contentPlan: {
    path: appRoutes.contentPlan,
    lazy: () => lazyRoute(() => import('src/pages/content-plan/ideas-page'), 'IdeasPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  benIdeas: {
    path: appRoutes.benIdeas,
    lazy: () =>
      lazyRoute(() => import('src/pages/content-plan/my-thoughts-page'), 'MyThoughtsPage'),
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  subscriptionCancelation: {
    path: appRoutes.subscriptionCancelation,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/subscription-cancelation/subscription-cancelation-page'),
        'SubscriptionCancelationPage',
      ),
    layout: SubscriptionCancelationLayout,
    restricted: [restricted.guest],
  },
  billingUpgrade: {
    path: appRoutes.billingUpgrade,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/billing/billing-upgrade-redirect-page'),
        'BillingUpgradeRedirectPage',
      ),
    layout: WrapperLayout,
    restricted: [restricted.guest],
  },
  billingPortal: {
    path: appRoutes.billingPortal,
    lazy: () =>
      lazyRoute(
        () => import('src/pages/billing/billing-portal-redirect-page'),
        'BillingPortalRedirectPage',
      ),
    layout: WrapperLayout,
    restricted: [restricted.guest],
  },
  status: {
    path: appRoutes.status,
    lazy: () => lazyRoute(() => import('src/pages/status/status-page'), 'StatusPage'),
    layout: WrapperLayout,
    restricted: [],
  },
  any: {
    path: appRoutes.any,
    lazy: () => lazyRoute(() => import('src/pages/NotFoundPage/NotFoundPage'), 'NotFoundPage'),
    layout: FullPageWrapper,
  },
};

export { routes };
